import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import Swal from 'sweetalert2';

import { UserService } from '../../../../services/user';
import { DentistService } from '../../../../services/dentist';

@Component({
  selector: 'app-alldentist',
  templateUrl: './alldentist.component.html',
  styleUrls: ['./alldentist.component.scss']
})
export class AlldentistComponent implements OnInit {

  displayedColumns: string[] = [
    'image',
    'name',
    'mobile',
    'specialty',
    'master_year',
    'phd_year',
    'certificates',
    'scfhs_level',
    'years',
    'score',
    'band',
    'cv',
    'portfolio',
    'call',
    'chat',
    'options'
  ];

  dataSource = new MatTableDataSource<any>([]);
  allData: any[] = [];

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  contentReady = false;
  noData = false;
  totalItems = 0;
  searchKey = '';
  isAdmin = false;
  canEdit = false;

  pageSize = 10;
  currentPage = 1;

  private readonly apiBaseUrl = 'https://backendtest.blueboxpet.com';

  constructor(
    private dentistService: DentistService,
    private userService: UserService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.userService.getAccountType().subscribe({
      next: res => {
        const accountType = String(res?.type ?? res?.user_type ?? '').trim();
        const userType = String(res?.user_type ?? '').trim().toLowerCase();
        const accessPages = Array.isArray(res?.access_pages) ? res.access_pages.map(String) : [];

        this.isAdmin = accountType === '1' || userType === 'admin';
        this.canEdit = this.isAdmin || accessPages.includes('7');
        this.getAllDentists(this.pageSize, this.currentPage);
      },
      error: () => {
        this.isAdmin = false;
        this.canEdit = false;
        this.getAllDentists(this.pageSize, this.currentPage);
      }
    });
  }

  // =======================
  // GET ALL
  // =======================
  getAllDentists(perPage: number, page: number): void {
    this.contentReady = false;
    this.noData = false;

    this.dentistService.getAll(perPage, page).subscribe({
      next: (res: any) => {
        const rows = this.extractRows(res);

        this.allData = rows.map((d: any) => this.mapDentistRow(d));

        this.dataSource.data = this.allData;

        this.totalItems = res?.total || res?.data?.total || this.allData.length;
        this.noData = this.allData.length === 0;

        this.pageSize = perPage;
        this.currentPage = page;

        this.contentReady = true;
      },
      error: () => {
        this.dataSource.data = [];
        this.allData = [];
        this.noData = true;
        this.contentReady = true;

        Swal.fire('خطأ', 'فشل تحميل بيانات الأطباء', 'error');
      }
    });
  }

  private extractRows(res: any): any[] {
    if (Array.isArray(res)) {
      return res;
    }

    if (Array.isArray(res?.data)) {
      return res.data;
    }

    if (Array.isArray(res?.data?.data)) {
      return res.data.data;
    }

    return [];
  }

  private mapDentistRow(d: any): any {
    const certificates = Array.isArray(d?.certificates) ? d.certificates : [];

    return {
      id: d?.id,

      name: d?.full_name || d?.name || '—',
      email: d?.email || '',
      mobile: d?.mobile || '',
      whatsapp_number: d?.whatsapp_number || '',

      specialty: this.getSpecialtyLabel(d?.specialty, d?.specialty_other),

      master_year: d?.master_year || null,
      phd_year: d?.phd_year || null,

      certificates_count: certificates.length,

      scfhs_level: this.mapScfhsLevel(d?.scfhs_classification_level),
      years_of_practice: this.mapYears(d?.years_of_practice),

      doctor_image_url: this.getFileUrl(d?.doctor_image_url || d?.doctor_image),

      total_score: d?.total_score,

      band: this.mapBand(d?.interpretation_band),
      band_label: this.mapBandLabel(d?.interpretation_band),

      cv_url: this.getFileUrl(d?.cv_url || d?.cv_pdf),

      portfolio_link: d?.portfolio_link || ''
    };
  }

  // =======================
  // URL HELPERS
  // =======================
  private getFileUrl(path: string | null | undefined): string | null {
    if (!path) return null;

    let value = String(path).trim();

    if (!value) return null;

    if (value.startsWith('http://') || value.startsWith('https://')) {
      if (value.includes('/storage/app/public/')) {
        return value;
      }

      if (value.includes('/storage/')) {
        return value.replace('/storage/', '/storage/app/public/');
      }

      return value;
    }

    value = value
      .replace(/^\/+/, '')
      .replace(/^storage\/app\/public\//, '')
      .replace(/^storage\//, '')
      .replace(/^app\/public\//, '')
      .replace(/^public\//, '');

    return `${this.apiBaseUrl}/storage/app/public/${value}`;
  }

  // =======================
  // Mapping helpers
  // =======================
  mapScfhsLevel(val: string): string {
    const map: any = {
      general_dentist: 'طبيب أسنان عام',
      registrar: 'أخصائي',
      resident: 'طبيب مقيم',
      senior_registrar: 'أخصائي أول',
      consultant: 'استشاري'
    };

    return map[val] || '—';
  }

  mapYears(val: string): string {
    const map: any = {
      less_than_1: 'أقل من سنة',
      '1_3': '1 – 3 سنوات',
      '4_7': '4 – 7 سنوات',
      more_than_7: 'أكثر من 7 سنوات'
    };

    return map[val] || '—';
  }

  mapBand(val: string): string {
    if (val === 'priority_shortlist') return 'priority';
    if (val === 'hold_further_review') return 'hold';
    return 'not';
  }

  mapBandLabel(val: string): string {
    if (val === 'priority_shortlist') return 'أولوية';
    if (val === 'hold_further_review') return 'مراجعة';
    if (val === 'not_suitable') return 'غير مناسب';
    return '—';
  }

  private getSpecialtyLabel(specialty: string, other?: string): string {
    if (!specialty) return '—';

    if (specialty === 'Other' && other) {
      return other;
    }

    const map: any = {
      'General Dentistry': 'طب الأسنان العام',
      'Family Dentistry': 'طب أسنان الأسرة',
      'Endodontics': 'علاج الجذور',
      'Prosthodontics': 'الاستعاضة السنية',
      'Periodontics': 'علاج اللثة',
      'Orthodontics': 'تقويم الأسنان',
      'Oral & Maxillofacial Surgery (OMFS)': 'جراحة الفم والوجه والفكين',
      'Oral Surgery': 'جراحة الفم',
      'Pediatric Dentistry': 'طب أسنان الأطفال',
      'Restorative Dentistry': 'طب الأسنان الترميمي',
      'Oral Medicine / Oral Pathology': 'طب وأمراض الفم',
      'Other': 'أخرى'
    };

    return map[specialty] || specialty;
  }

  // =======================
  // SEARCH
  // =======================
  search(): void {
    const key = this.searchKey.trim().toLowerCase();

    if (!key) {
      this.dataSource.data = this.allData;
      this.noData = this.allData.length === 0;
      return;
    }

    const filtered = this.allData.filter(d =>
      String(d.name || '').toLowerCase().includes(key) ||
      String(d.email || '').toLowerCase().includes(key) ||
      String(d.mobile || '').includes(key) ||
      String(d.whatsapp_number || '').includes(key) ||
      String(d.specialty || '').toLowerCase().includes(key)
    );

    this.dataSource.data = filtered;
    this.noData = filtered.length === 0;
  }

  clearSearch(): void {
    this.searchKey = '';
    this.dataSource.data = this.allData;
    this.noData = this.allData.length === 0;
  }

  refresh(): void {
    this.searchKey = '';
    this.getAllDentists(this.pageSize, 1);
  }

  // =======================
  // PAGINATION
  // =======================
  onPaginateChange(e: PageEvent): void {
    this.getAllDentists(e.pageSize, e.pageIndex + 1);
  }

  // =======================
  // ACTIONS
  // =======================
  details(id: number): void {
    this.router.navigate(['/dentist', id]);
  }

  edit(id: number): void {
    this.router.navigate(['/dentistform'], {
      queryParams: { id }
    });
  }

  delete(id: number): void {
    Swal.fire({
      title: 'هل أنت متأكد؟',
      text: 'سيتم حذف هذا التسجيل نهائيًا',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'نعم، حذف',
      cancelButtonText: 'إلغاء',
      confirmButtonColor: '#dc3545'
    }).then(result => {
      if (!result.isConfirmed) return;

      this.dentistService.delete(id).subscribe({
        next: () => {
          Swal.fire('تم الحذف', 'تم حذف التسجيل بنجاح', 'success');
          this.getAllDentists(this.pageSize, this.currentPage);
        },
        error: () => {
          Swal.fire('خطأ', 'فشل حذف التسجيل', 'error');
        }
      });
    });
  }

  call(phone: string): void {
    const cleanPhone = this.cleanPhone(phone);

    if (!cleanPhone) {
      Swal.fire('تنبيه', 'لا يوجد رقم هاتف صحيح', 'warning');
      return;
    }

    window.open(`tel:+${cleanPhone}`, '_blank');
  }

  chat(phone: string): void {
    const cleanPhone = this.cleanEgyptPhone(phone);

    if (!cleanPhone) {
      Swal.fire('تنبيه', 'لا يوجد رقم واتساب صحيح', 'warning');
      return;
    }

    window.open(`https://api.whatsapp.com/send?phone=${cleanPhone}`, '_blank');
  }

  private cleanPhone(phone: string): string {
    return String(phone || '').replace(/\D/g, '');
  }

  private cleanEgyptPhone(phone: string): string {
    let value = this.cleanPhone(phone);

    if (!value) return '';

    if (value.startsWith('20')) {
      return value;
    }

    if (value.startsWith('0')) {
      return `20${value.substring(1)}`;
    }

    return `20${value}`;
  }
}
